/**
 * usePersistentBoolean — a boolean state persisted to localStorage across reloads.
 *
 * Same namespacing / SSR-safety as usePersistentNumber (key prefix `seg:bool:`).
 * Used for the "close picker after adding" preference.
 */

import { useCallback, useState } from 'react';

const KEY_PREFIX = 'seg:bool:';

function readStored(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${KEY_PREFIX}${key}`);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${key}`, value ? '1' : '0');
  } catch {
    /* storage disabled — silently no-op */
  }
}

export function usePersistentBoolean(
  key: string,
  defaultValue: boolean,
): [boolean, (value: boolean) => void] {
  const [value, setValueState] = useState<boolean>(() => readStored(key, defaultValue));
  const setValue = useCallback(
    (next: boolean) => {
      setValueState(next);
      writeStored(key, next);
    },
    [key],
  );
  return [value, setValue];
}
